import { z } from "zod";

/**
 * The 6 canonical Mars Men personas. Mirrors mars-men-lp-generator/static/audience_personas.md.
 * A quiz classifier MUST return one of these keys.
 */
export const PERSONA_KEYS = [
  "trt-alternative",
  "aging-men",
  "skeptical-buyer",
  "everyday-man",
  "performance",
  "glp1-user",
] as const;

export const PersonaKeySchema = z.enum(PERSONA_KEYS);
export type PersonaKey = z.infer<typeof PersonaKeySchema>;

export const AnswerValueSchema = z.union([
  z.string(),
  z.array(z.string()),
  z.number(),
  z.null(),
]);
export type AnswerValue = z.infer<typeof AnswerValueSchema>;

export const AnswersSchema = z.record(z.string(), AnswerValueSchema);
export type Answers = z.infer<typeof AnswersSchema>;

const OptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
  emoji: z.string().optional(),
});
export type QuestionOption = z.infer<typeof OptionSchema>;

const WhyWeAskSchema = z.object({
  heading: z.string().default("Why we ask"),
  body: z.string().min(1),
});
export type WhyWeAsk = z.infer<typeof WhyWeAskSchema>;

const SingleQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("single"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  options: z.array(OptionSchema).min(2),
  whyWeAsk: WhyWeAskSchema.optional(),
});

const MultiQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("multi"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  options: z.array(OptionSchema).min(2),
  min: z.number().int().min(0).optional(),
  max: z.number().int().min(1).optional(),
  whyWeAsk: WhyWeAskSchema.optional(),
});

const SliderQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("slider"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  min: z.number(),
  max: z.number(),
  step: z.number().optional(),
  minLabel: z.string().optional(),
  maxLabel: z.string().optional(),
  whyWeAsk: WhyWeAskSchema.optional(),
});

const TextQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("text"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  placeholder: z.string().optional(),
  whyWeAsk: WhyWeAskSchema.optional(),
});

/**
 * Info screen: educational interstitial between questions. Renders rich content
 * with a Continue button. Stores `null` in answers so it doesn't influence the
 * classifier. Use to build conviction between answer pages.
 */
const InfoQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("info"),
  heading: z.string().min(1),
  body: z.array(z.string().min(1)).min(1),
  image: z
    .object({
      src: z.string().min(1),
      alt: z.string().default(""),
    })
    .optional(),
  animation: z.enum(["analyzing"]).optional(),
  autoAdvanceMs: z.number().int().min(0).optional(),
  cta: z.string().optional(),
});

export const QuestionSchema = z.discriminatedUnion("type", [
  SingleQuestionSchema,
  MultiQuestionSchema,
  SliderQuestionSchema,
  TextQuestionSchema,
  InfoQuestionSchema,
]);
export type Question = z.infer<typeof QuestionSchema>;

export const CaptureConfigSchema = z.object({
  fields: z.array(z.enum(["email", "phone"])).min(1),
  required: z.array(z.enum(["email", "phone"])).default([]),
  headline: z.string().optional(),
  subheadline: z.string().optional(),
  cta: z.string().optional(),
  disclaimer: z.string().optional(),
});
export type CaptureConfig = z.infer<typeof CaptureConfigSchema>;

/**
 * Quiz definition. Branching and classifier are pure functions of `answers`.
 *
 * - branching(answers, currentQuestionId): the id of the next question, or null
 *   if the questions phase is complete (advances to the email gate).
 * - classifier(answers): the persona key to assign to this submission.
 */
export const QuizMetaSchema = z.object({
  title: z.string().min(1),
  introHeadline: z.string().min(1),
  introBody: z.string().optional(),
});
export type QuizMeta = z.infer<typeof QuizMetaSchema>;

export const QuizDefinitionSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1).max(64).regex(/^[a-z0-9-]+$/),
  meta: QuizMetaSchema,
  questions: z.array(QuestionSchema).min(1),
  capture: CaptureConfigSchema,
});

export type QuizDefinition = z.infer<typeof QuizDefinitionSchema>;

/**
 * T-suppression profile dimensions. Derived from `answers` via
 * `quiz.deriveProfile(answers)` and rendered on the result page above the
 * persona-static hero/education content. The profile is the "We analyzed your X"
 * personalized-plan layer; the persona content is the UMP -> UMS bridge.
 *
 * Source of truth for the underlying medicine: `docs/t-suppression-research.md`.
 * Severity scale uses HIGH = highly suppressed (i.e. "level of T suppression"),
 * not high T -- match the meter on the result-page mock.
 */
export const SUPPRESSION_LEVELS = ["low", "normal", "medium", "high"] as const;
export const SuppressionLevelSchema = z.enum(SUPPRESSION_LEVELS);
export type SuppressionLevel = z.infer<typeof SuppressionLevelSchema>;

export const DECLINE_PATTERNS = [
  "stable",
  "early-onset",
  "acute",
  "progressive",
  "stalled-recovery",
] as const;
export const DeclinePatternSchema = z.enum(DECLINE_PATTERNS);
export type DeclinePattern = z.infer<typeof DeclinePatternSchema>;

export const ROOT_CAUSES = [
  "cortisol-dominance",
  "chronic-stress",
  "sleep-deprivation",
  "visceral-adiposity",
  "metabolic-syndrome",
  "lifestyle-load",
  "age-related-decline",
  "overtraining",
  "endocrine-disruption",
] as const;
export const RootCauseSchema = z.enum(ROOT_CAUSES);
export type RootCause = z.infer<typeof RootCauseSchema>;

export const QOL_IMPACT_LEVELS = ["low", "medium", "high"] as const;
export const QolImpactSchema = z.enum(QOL_IMPACT_LEVELS);
export type QolImpact = z.infer<typeof QolImpactSchema>;

/**
 * Evidence chips surface the user's actual quiz answers under the meter so the
 * profile reads as "we used your data" rather than a generic verdict. Each
 * item is a (label, value) pair rendered as a small pill.
 */
export const EvidenceItemSchema = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
});
export type EvidenceItem = z.infer<typeof EvidenceItemSchema>;

/**
 * Personalized "what the user wants from this protocol" pair, derived from the
 * results-want question. Surfaces under the assessment card so every collected
 * answer pays back personalization on the result page.
 */
export const ExpectedOutcomeSchema = z.object({
  label: z.string().min(1),
  copy: z.string().min(1),
});
export type ExpectedOutcome = z.infer<typeof ExpectedOutcomeSchema>;

export const TSuppressionProfileSchema = z.object({
  suppressionLevel: SuppressionLevelSchema,
  assessment: z.object({
    label: z.string().min(1),
    headline: z.string().min(1),
    explanation: z.string().min(1),
  }),
  declinePattern: DeclinePatternSchema,
  rootCauses: z.array(RootCauseSchema).min(2).max(3),
  qualityOfLifeImpact: QolImpactSchema,
  evidence: z.array(EvidenceItemSchema).min(2).max(6),
  expectedOutcome: ExpectedOutcomeSchema.optional(),
});
export type TSuppressionProfile = z.infer<typeof TSuppressionProfileSchema>;

export type Quiz = QuizDefinition & {
  branching: (answers: Answers, currentQuestionId: string) => string | null;
  classifier: (answers: Answers) => PersonaKey;
  deriveProfile?: (answers: Answers) => TSuppressionProfile;
};

/**
 * Defines a quiz with full type-checking. Use this in quizzes/<slug>.ts.
 *
 * Example:
 *   export const igniteQuiz = defineQuiz({
 *     id: "ignite",
 *     slug: "ignite",
 *     meta: { ... },
 *     questions: [ ... ],
 *     capture: { ... },
 *     branching: (answers, current) => "next-id",
 *     classifier: (answers) => "trt-alternative",
 *   });
 */
export function defineQuiz(quiz: Quiz): Quiz {
  QuizDefinitionSchema.parse({
    id: quiz.id,
    slug: quiz.slug,
    meta: quiz.meta,
    questions: quiz.questions,
    capture: quiz.capture,
  });
  return quiz;
}
